import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Briefcase, 
  Calendar,
  FileText,
  CheckCircle,
  Clock,
  Save
} from 'lucide-react';

const ApplicationDetailModal = ({ application, onClose, onStatusUpdate }) => {
  const [selectedStatus, setSelectedStatus] = useState(application.status);
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const qualificationLabels = {
    'phd': 'PhD / Doctorate',
    'masters': "Master's Degree",
    'postgrad-diploma': 'Postgraduate Diploma',
    'bachelors': "Bachelor's Degree",
    'diploma': 'Diploma',
    'certificate': 'Certificate III/IV',
    'secondary': 'Year 12 / HSC',
    'other': 'Other'
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onStatusUpdate(application.applicationId, selectedStatus, notes);
    } finally {
      setIsSaving(false);
    }
  };

  const statusOptions = [
    { value: 'submitted', label: 'Submitted', color: 'blue' },
    { value: 'reviewing', label: 'Reviewing', color: 'yellow' },
    { value: 'approved', label: 'Approved', color: 'green' },
    { value: 'rejected', label: 'Rejected', color: 'red' },
    { value: 'on-hold', label: 'On Hold', color: 'gray' }
  ];

  const data = application.applicationData || {};
  const isDriver = data.positionAppliedFor === 'Driver';
  const isPhysiotherapist = data.positionAppliedFor === 'Physiotherapist';
  const isHealthcareAssistant = data.positionAppliedFor === 'Healthcare Assistant';

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full my-8 relative max-h-[90vh] overflow-y-auto"
        >
          <div className="sticky top-0 bg-white border-b border-bnBorder px-8 py-6 flex items-center justify-between z-10">
            <div>
              <h2 className="font-display font-light text-2xl text-bnInk">
                {application.applicantName}
              </h2>
              <p className="text-sm text-bnBody">{application.email}</p>
            </div>
            <button
              onClick={onClose}
              className="text-bnBody hover:text-bnInk transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          <div className="p-8 space-y-8">
            <div className="bg-bnCream rounded-2xl p-6">
              <h3 className="font-display font-normal text-lg text-bnInk mb-4">Update Status</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-bnInk mb-2">
                    Application Status
                  </label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="w-full px-4 py-2 bg-white border border-bnBorder rounded-lg focus:outline-none focus:border-bnAccent transition-colors"
                  >
                    {statusOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-bnInk mb-2">
                    Admin Notes (Optional)
                  </label>
                  <input
                    type="text"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add notes..."
                    className="w-full px-4 py-2 bg-white border border-bnBorder rounded-lg focus:outline-none focus:border-bnAccent transition-colors"
                  />
                </div>
              </div>
              <button
                onClick={handleSave}
                disabled={isSaving || selectedStatus === application.status}
                className="mt-4 inline-flex items-center gap-2 bg-bnAccent hover:bg-bnAccentDark text-white font-medium text-sm px-7 py-3 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    Save Changes
                  </>
                )}
              </button>
            </div>

            <div>
              <h3 className="font-display font-normal text-lg text-bnInk mb-4 pb-2 border-b border-bnBorder">
                Personal Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <User className="text-bnAccent mt-1" size={20} />
                  <div>
                    <p className="text-sm text-bnBody">Full Name</p>
                    <p className="text-base text-bnInk">{data.firstName} {data.lastName}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="text-bnAccent mt-1" size={20} />
                  <div>
                    <p className="text-sm text-bnBody">Email</p>
                    <p className="text-base text-bnInk">{application.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="text-bnAccent mt-1" size={20} />
                  <div>
                    <p className="text-sm text-bnBody">Phone</p>
                    <p className="text-base text-bnInk">{data.phone || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="text-bnAccent mt-1" size={20} />
                  <div>
                    <p className="text-sm text-bnBody">Current Country</p>
                    <p className="text-base text-bnInk">{data.currentCountry || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="text-bnAccent mt-1" size={20} />
                  <div>
                    <p className="text-sm text-bnBody">Nationality</p>
                    <p className="text-base text-bnInk">{data.nationality || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Calendar className="text-bnAccent mt-1" size={20} />
                  <div>
                    <p className="text-sm text-bnBody">Applied On</p>
                    <p className="text-base text-bnInk">
                      {new Date(application.createdAt).toLocaleDateString('en-AU', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-display font-normal text-lg text-bnInk mb-4 pb-2 border-b border-bnBorder">
                Position Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <Briefcase className="text-bnAccent mt-1" size={20} />
                  <div>
                    <p className="text-sm text-bnBody">Position Applied For</p>
                    <p className="text-base text-bnInk">{data.positionAppliedFor || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Calendar className="text-bnAccent mt-1" size={20} />
                  <div>
                    <p className="text-sm text-bnBody">Preferred Start Date</p>
                    <p className="text-base text-bnInk">{data.startDate || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <FileText className="text-bnAccent mt-1" size={20} />
                  <div>
                    <p className="text-sm text-bnBody">Employment Type</p>
                    <p className="text-base text-bnInk">
                      {Array.isArray(data.employmentType) && data.employmentType.length > 0
                        ? data.employmentType.join(', ')
                        : 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="text-bnAccent mt-1" size={20} />
                  <div>
                    <p className="text-sm text-bnBody">Availability</p>
                    <p className="text-base text-bnInk">
                      {Array.isArray(data.availability) && data.availability.length > 0
                        ? data.availability.join(', ')
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-display font-normal text-lg text-bnInk mb-4 pb-2 border-b border-bnBorder">
                Work Authorisation
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-bnAccent mt-1" size={20} />
                  <div>
                    <p className="text-sm text-bnBody">Right to Work in Australia</p>
                    <p className="text-base text-bnInk capitalize">{data.rightToWork || 'N/A'}</p>
                  </div>
                </div>
                {data.workPermitStatus && (
                  <div className="flex items-start gap-3">
                    <FileText className="text-bnAccent mt-1" size={20} />
                    <div>
                      <p className="text-sm text-bnBody">Visa / Work Permit Status</p>
                      <p className="text-base text-bnInk">{data.workPermitStatus}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div>
              <h3 className="font-display font-normal text-lg text-bnInk mb-4 pb-2 border-b border-bnBorder">
                Qualifications & Experience
              </h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-bnBody mb-1">Highest Academic Qualification</p>
                  <p className="text-base text-bnInk">
                    {data.qualification ? (qualificationLabels[data.qualification] || data.qualification) : 'N/A'}
                  </p>
                </div>
                {data.fieldOfStudy && (
                  <div>
                    <p className="text-sm text-bnBody mb-1">Field of Study / Program</p>
                    <p className="text-base text-bnInk">{data.fieldOfStudy}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-bnBody mb-1">Country Where Qualification Was Obtained</p>
                  <p className="text-base text-bnInk">{data.qualificationCountry || 'N/A'}</p>
                </div>

                {isHealthcareAssistant && (
                  <>
                    <div>
                      <p className="text-sm text-bnBody mb-1">Care-Related Certifications</p>
                      <p className="text-base text-bnInk capitalize">{data.hasCareTraining || 'N/A'}</p>
                    </div>
                    {data.hasCareTraining === 'yes' && data.careTrainingDetails && (
                      <div>
                        <p className="text-sm text-bnBody mb-1">Care Training Details</p>
                        <p className="text-base text-bnInk whitespace-pre-wrap">{data.careTrainingDetails}</p>
                      </div>
                    )}
                  </>
                )}

                {isPhysiotherapist && (
                  <>
                    <div>
                      <p className="text-sm text-bnBody mb-1">AHPRA Registration Status</p>
                      <p className="text-base text-bnInk capitalize">{data.ahpraRegistered || 'N/A'}</p>
                    </div>
                    {data.ahpraRegistered === 'yes' && data.registrationNumber && (
                      <div>
                        <p className="text-sm text-bnBody mb-1">AHPRA Registration Number</p>
                        <p className="text-base text-bnInk">{data.registrationNumber}</p>
                      </div>
                    )}
                  </>
                )}

                {isDriver && (
                  <>
                    <div>
                      <p className="text-sm text-bnBody mb-1">Australian Driver's Licence</p>
                      <p className="text-base text-bnInk capitalize">{data.drivingLicence || 'N/A'}</p>
                    </div>
                    {data.drivingLicence === 'yes' && (
                      <>
                        <div>
                          <p className="text-sm text-bnBody mb-1">Licence Class</p>
                          <p className="text-base text-bnInk">{data.licenceClass || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-bnBody mb-1">Years of Driving Experience</p>
                          <p className="text-base text-bnInk">{data.yearsDriverExperience || 'N/A'}</p>
                        </div>
                      </>
                    )}
                  </>
                )}

                <div>
                  <p className="text-sm text-bnBody mb-1">Willing to Complete Required Training</p>
                  <p className="text-base text-bnInk capitalize">{data.willingToTrain || 'N/A'}</p>
                </div>

                {isHealthcareAssistant && (
                  <>
                    <div>
                      <p className="text-sm text-bnBody mb-1">Years of Care Experience</p>
                      <p className="text-base text-bnInk">{data.yearsExperience || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-bnBody mb-1">Care Settings Experience</p>
                      <p className="text-base text-bnInk">
                        {Array.isArray(data.careSettings) && data.careSettings.length > 0
                          ? data.careSettings.join(', ')
                          : 'N/A'}
                      </p>
                    </div>
                  </>
                )}

                {isPhysiotherapist && (
                  <div>
                    <p className="text-sm text-bnBody mb-1">Years of Physiotherapy Experience</p>
                    <p className="text-base text-bnInk">{data.yearsPhysioExperience || 'N/A'}</p>
                  </div>
                )}

                {data.careExperienceDescription && (
                  <div>
                    <p className="text-sm text-bnBody mb-1">Experience Description</p>
                    <p className="text-base text-bnInk whitespace-pre-wrap">{data.careExperienceDescription}</p>
                  </div>
                )}
              </div>
            </div>

            {((Array.isArray(data.skills) && data.skills.length > 0) || 
              (Array.isArray(data.specializations) && data.specializations.length > 0)) && (
              <div>
                <h3 className="font-display font-normal text-lg text-bnInk mb-4 pb-2 border-b border-bnBorder">
                  {isPhysiotherapist ? 'Specialisations' : 'Skills'}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {(data.skills || data.specializations || []).map((item, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-bnAccentSoft border border-bnAccent text-bnAccentDark rounded-full text-sm"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h3 className="font-display font-normal text-lg text-bnInk mb-4 pb-2 border-b border-bnBorder">
                Motivation
              </h3>
              <div className="space-y-4">
                {data.whyJoinUs && (
                  <div>
                    <p className="text-sm text-bnBody mb-1">Why B & N Enterprise?</p>
                    <p className="text-base text-bnInk whitespace-pre-wrap">{data.whyJoinUs}</p>
                  </div>
                )}
                {data.whyAustralia && (
                  <div>
                    <p className="text-sm text-bnBody mb-1">Why Australia?</p>
                    <p className="text-base text-bnInk whitespace-pre-wrap">{data.whyAustralia}</p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <h3 className="font-display font-normal text-lg text-bnInk mb-4 pb-2 border-b border-bnBorder">
                Background Checks
              </h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-bnBody mb-1">National Police Check</p>
                  <p className="text-base text-bnInk capitalize">{data.policeClearance || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-bnBody mb-1">Working with Children Check</p>
                  <p className="text-base text-bnInk capitalize">{data.workingWithChildrenCheck || 'N/A'}</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-display font-normal text-lg text-bnInk mb-4 pb-2 border-b border-bnBorder">
                Uploaded Documents
              </h3>
              <div className="space-y-2">
                {data.cvFile && (
                  <div className="flex items-center gap-2 text-sm">
                    <FileText size={16} className="text-bnAccent" />
                    <span className="text-bnInk">CV: {data.cvFile}</span>
                  </div>
                )}
                {!data.cvFile && (
                  <p className="text-sm text-bnBody">No CV uploaded</p>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ApplicationDetailModal;
